import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown, Globe } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const Logo = () => {
  const [animKey, setAnimKey] = useState(0);
  const [animating, setAnimating] = useState(false);

  const barWidths = [30, 38, 44, 38, 30];
  const barY = [6, 14, 22, 30, 38];

  const handleMouseEnter = () => { setAnimKey(k => k + 1); setAnimating(true); };
  const handleMouseLeave = () => { setAnimating(false); };

  return (
    <Link to="/engineering" className="flex items-center gap-3 group" data-testid="logo-link"
      onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <svg width="36" height="36" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Durable Designs logo">
        {barWidths.map((w, i) => (
          <rect key={animating ? `${i}-${animKey}` : i} x="2" y={barY[i]} width={w} height="4" rx="1" fill="#15803D" className="logo-bar"
            style={animating ? { transformOrigin: 'left center', animation: `barReveal 220ms cubic-bezier(0.16, 1, 0.3, 1) ${i * 60}ms both` } : {}} />
        ))}
      </svg>
      <span className="text-[17px] font-semibold tracking-[0.12em] uppercase text-gray-900 whitespace-nowrap" style={{fontFamily:'Outfit,sans-serif'}}>
        Durable Designs
      </span>
    </Link>
  );
};

const DropdownItem = ({ to, children, onClick }) => (
  <Link to={to} onClick={onClick}
    className="block px-4 py-2.5 text-sm text-gray-600 hover:text-green-700 hover:bg-green-50 transition-colors"
    style={{fontFamily:'IBM Plex Sans,sans-serif'}}>
    {children}
  </Link>
);

const NavDropdown = ({ label, items, isActive }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 px-3 py-2 text-sm font-medium transition-colors ${isActive ? 'text-green-700' : 'text-gray-600 hover:text-green-700'}`}
        style={{fontFamily:'IBM Plex Sans,sans-serif'}} data-testid={`nav-dropdown-${label.toLowerCase().replace(/\s/g,'-')}`}>
        {label}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 shadow-sm z-50">
          {items.map((item) => (
            <DropdownItem key={item.path} to={item.path} onClick={() => setOpen(false)}>{item.name}</DropdownItem>
          ))}
        </div>
      )}
    </div>
  );
};

const LangSwitcher = () => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = i18n.language === 'pt' ? 'PT' : 'EN';

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const switchLang = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem('dd-lang', lng);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative ml-2">
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2 py-1.5 text-sm text-gray-500 hover:text-green-700 transition-colors"
        data-testid="lang-switcher">
        <Globe className="w-3.5 h-3.5" />
        {current}
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-1 w-28 bg-white border border-gray-200 shadow-sm z-50">
          <button onClick={() => switchLang('en')}
            className={`block w-full text-left px-4 py-2 text-sm transition-colors ${i18n.language === 'en' ? 'text-green-700 bg-green-50' : 'text-gray-600 hover:bg-green-50'}`}>
            English
          </button>
          <button onClick={() => switchLang('pt')}
            className={`block w-full text-left px-4 py-2 text-sm transition-colors ${i18n.language === 'pt' ? 'text-green-700 bg-green-50' : 'text-gray-600 hover:bg-green-50'}`}>
            Português
          </button>
        </div>
      )}
    </div>
  );
};

export const Header = () => {
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const location = useLocation();

  const isPathActive = (paths) => paths.some(p => location.pathname.startsWith(p));

  const navStructure = [
    {
      label: t('nav.services'), type: 'dropdown', activePaths: ['/engineering/services'],
      items: [
        { name: 'Materials Consulting', path: '/engineering/services/materials' },
        { name: 'Infrastructure Risk', path: '/engineering/services/infrastructure-risk' },
        { name: 'Digital & AI Solutions', path: '/engineering/services/digital' },
      ]
    },
    { label: t('nav.projects'), type: 'link', path: '/engineering/projects', activePaths: ['/engineering/projects'] },
    { label: t('nav.apps'), type: 'link', path: '/engineering/apps', activePaths: ['/engineering/apps', '/engineering/identity-testing', '/engineering/carbonation-risk', '/engineering/prioris-app'] },
    { label: t('nav.knowledge'), type: 'link', path: '/engineering/knowledge', activePaths: ['/engineering/knowledge'] },
    {
      label: t('nav.people'), type: 'dropdown', activePaths: ['/engineering/people'],
      items: [
        { name: t('nav.our_people'), path: '/engineering/people' },
        { name: t('nav.careers'), path: '/engineering/people/careers' },
      ]
    },
    { label: t('nav.contact'), type: 'link', path: '/engineering/contact', activePaths: ['/engineering/contact'] },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200" data-testid="main-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Logo />
          <div className="hidden lg:flex items-center">
            <nav className="flex items-center gap-1" data-testid="desktop-nav">
              {navStructure.map((item) =>
                item.type === 'dropdown' ? (
                  <NavDropdown key={item.label} label={item.label} items={item.items} isActive={isPathActive(item.activePaths)} />
                ) : (
                  <Link key={item.label} to={item.path}
                    className={`px-3 py-2 text-sm font-medium transition-colors ${isPathActive(item.activePaths) ? 'text-green-700' : 'text-gray-600 hover:text-green-700'}`}
                    style={{fontFamily:'IBM Plex Sans,sans-serif'}} data-testid={`nav-link-${item.label.toLowerCase()}`}>
                    {item.label}
                  </Link>
                )
              )}
            </nav>
            <div className="border-l border-gray-200 ml-3 pl-1 flex items-center gap-1">
              <Link
                to="/"
                className="px-2 py-1.5 text-xs tracking-[0.1em] uppercase text-gray-500 hover:text-green-700 border border-gray-200 transition-colors"
                style={{fontFamily:'IBM Plex Sans,sans-serif'}}
                data-testid="eng-nav-home"
              >
                Home
              </Link>
              <LangSwitcher />
            </div>
          </div>

          <div className="flex items-center gap-2 lg:hidden">
            <LangSwitcher />
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="p-2 text-gray-700 hover:text-green-700" data-testid="mobile-menu-toggle">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-gray-200 bg-white" data-testid="mobile-menu">
          <nav className="px-4 py-3">
            {navStructure.map((item) =>
              item.type === 'dropdown' ? (
                <div key={item.label} className="border-b border-gray-100 last:border-0">
                  <button onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                    className="flex items-center justify-between w-full py-3 text-sm font-medium text-gray-700" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>
                    {item.label}
                    <ChevronDown className={`w-4 h-4 transition-transform ${mobileExpanded === item.label ? 'rotate-180' : ''}`} />
                  </button>
                  {mobileExpanded === item.label && (
                    <div className="pb-2 pl-4">
                      {item.items.map((sub) => (
                        <Link key={sub.path} to={sub.path} onClick={() => { setMobileMenuOpen(false); setMobileExpanded(null); }}
                          className="block py-2 text-sm text-gray-500 hover:text-green-700" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link key={item.label} to={item.path} onClick={() => setMobileMenuOpen(false)}
                  className="block py-3 text-sm font-medium text-gray-700 border-b border-gray-100 last:border-0" style={{fontFamily:'IBM Plex Sans,sans-serif'}}>
                  {item.label}
                </Link>
              )
            )}
          </nav>
        </div>
      )}
    </header>
  );
};
